
import { useState, useEffect, forwardRef, ForwardedRef } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Search, Edit, Trash2, Star, StarOff, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { BlogPostEditor } from "./BlogPostEditor";

interface BlogPost {
  id: string;
  title: string;
  description: string;
  category: string;
  featured: boolean;
  image_url: string | null;
  video_url: string | null;
  published_at: string;
  created_at: string;
}

export const BlogPostsList = forwardRef((props, ref: ForwardedRef<{ fetchPosts: () => Promise<void> }>) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState<string | null>(null);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  
  // Fetch posts from the database
  const fetchPosts = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      setPosts(data || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error(t('blog.admin.loadFailed'));
    } finally {
      setLoading(false);
    }
  };
  
  // Expose the fetchPosts method via the forwarded ref
  if (ref) {
    // @ts-ignore - This is a workaround for ref types
    ref.current = {
      fetchPosts
    };
  }
  
  // Fetch posts on component mount
  useEffect(() => {
    fetchPosts();
  }, []);

  // Filter posts based on search query
  const filteredPosts = posts.filter(post => {
    const searchString = searchQuery.toLowerCase();
    return post.title.toLowerCase().includes(searchString) || 
           post.description.toLowerCase().includes(searchString) ||
           post.category.toLowerCase().includes(searchString);
  });

  const handleDeleteClick = (postId: string) => {
    setPostToDelete(postId);
    setDeleteDialogOpen(true);
  };

  const handleEditClick = (post: BlogPost) => {
    setEditingPost(post);
    setEditDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (postToDelete === null) return;
    
    try {
      // Delete the post from the database
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', postToDelete);
      
      if (error) throw error;
      
      // Remove the post from the local state
      setPosts(posts.filter(post => post.id !== postToDelete));
      toast.success(t('blog.admin.postDeleted'));
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error(t('blog.admin.deleteFailed'));
    } finally {
      setDeleteDialogOpen(false);
      setPostToDelete(null);
    }
  };

  const toggleFeatured = async (postId: string, currentStatus: boolean) => {
    try {
      // Update the featured status in the database
      const { error } = await supabase
        .from('blog_posts')
        .update({ featured: !currentStatus })
        .eq('id', postId);
      
      if (error) throw error;
      
      // Update the post in the local state
      setPosts(posts.map(post => {
        if (post.id === postId) {
          return { ...post, featured: !currentStatus };
        }
        return post;
      }));
      
      toast.success(t('blog.admin.featureToggled'));
    } catch (error) {
      console.error('Error toggling featured status:', error);
      toast.error(t('blog.admin.updateFailed'));
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-4">
      <div className="relative w-full max-w-sm mb-6">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <Input 
          className="pl-10" 
          placeholder={t('blog.admin.searchPosts')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
      
      {loading ? (
        <div className="text-center py-12">
          <p>{t('common.loading')}</p>
        </div>
      ) : filteredPosts.length > 0 ? (
        <div className="space-y-4">
          {filteredPosts.map(post => (
            <Card key={post.id} className="overflow-hidden">
              <CardContent className="p-0">
                <div className="flex flex-col md:flex-row">
                  <div className="w-full md:w-24 h-24 bg-muted flex-shrink-0">
                    {post.image_url ? (
                      <img 
                        src={post.image_url} 
                        alt={post.title} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-muted">
                        <AlertCircle className="h-6 w-6 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div className="p-4 flex-grow">
                    <div className="flex flex-col md:flex-row justify-between">
                      <div>
                        <h3 className="font-semibold text-lg">{post.title}</h3>
                        <p className="text-muted-foreground text-sm mt-1">{post.category} • {formatDate(post.published_at)}</p>
                      </div>
                      <div className="flex space-x-2 mt-2 md:mt-0">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => toggleFeatured(post.id, post.featured)}
                          title={post.featured ? t('blog.admin.unfeature') : t('blog.admin.feature')}
                        >
                          {post.featured ? <Star className="h-4 w-4 text-yellow-500" /> : <StarOff className="h-4 w-4" />}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleEditClick(post)}
                          title={t('common.edit')}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleDeleteClick(post.id)}
                          title={t('common.delete')}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm mt-2 line-clamp-2">{post.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 border rounded-md bg-muted/20">
          <p className="text-muted-foreground">{t('blog.admin.noPostsFound')}</p>
        </div>
      )}
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('blog.admin.confirmDelete')}</DialogTitle>
            <DialogDescription>{t('blog.admin.deleteWarning')}</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-2 mt-4">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              {t('common.delete')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Post Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[900px]">
          <DialogHeader>
            <DialogTitle>{t('blog.admin.editPost')}</DialogTitle>
          </DialogHeader>
          {editingPost && (
            <BlogPostEditor 
              post={editingPost} 
              onPostSaved={() => {
                setEditDialogOpen(false);
                fetchPosts();
              }} 
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
});

BlogPostsList.displayName = "BlogPostsList";
